import React from "react";
import clsx from "clsx";
import {
  Contact,
  DollarSign,
  Ticket,
  UserPlus,
  Users,
  Folder,
  Database,
  Calendar,
  CalendarDays,
  File,
  Book,
  Mail,
  MessageCircle,
  GitBranch,
} from "lucide-react";
import {
  APICategory,
  BlueprintSpeed,
  Integration,
  UnreleasedAPICategory,
  User,
  UserType,
} from "./Entities";
import { ReactComponent as MarketingAutomationIcon } from "../assets/svg/marketing-automation-feather.svg";
import moment from "moment-timezone";
import DOMPurify from "dompurify";
import { Text } from "@merge-api/merge-javascript-shared";
import { TextBaseProps } from "@merge-api/merge-javascript-shared/dist/designSystem/typography/TextBase/TextBase";

const INTEGRATION_CATEGORY_LIST = [
  APICategory.hris,
  APICategory.ats,
  APICategory.accounting,
  APICategory.ticketing,
  APICategory.crm,
  APICategory.filestorage,
  APICategory.knowledgebase,
  APICategory.chat,
];

// Use when releasing a new category in closed beta. Make sure to add it to the function below
export const CLOSED_BETA_CATEGORY_LIST: APICategory[] = [];

// List of Public categories that are Beta
// We want to publicly release these categories, but still let customers know that they are at a beta-level of readiness
export const BETA_PUBLIC_CATEGORIES: APICategory[] = [APICategory.chat];

// Unreleased categories that are in beta — shown with a Beta badge in the dashboard
export const BETA_UNRELEASED_CATEGORIES: UnreleasedAPICategory[] = [
  UnreleasedAPICategory.email,
  UnreleasedAPICategory.calendar,
  UnreleasedAPICategory.versioncontrol,
];

export const isBetaCategory = (category: APICategory | UnreleasedAPICategory): boolean =>
  BETA_PUBLIC_CATEGORIES.includes(category as APICategory) ||
  CLOSED_BETA_CATEGORY_LIST.includes(category as APICategory) ||
  BETA_UNRELEASED_CATEGORIES.includes(category as UnreleasedAPICategory);

// This is the source of truth for what is active.
// Changing this list changes what appears in the dashboard.
// It triggers updates in Get Started, Configuration (multiple tabs), and code snippets.
export const getIntegrationCategoryList = (user?: User): APICategory[] => {
  return [
    ...INTEGRATION_CATEGORY_LIST,
    // cast until email/calendar are public
    ...(user?.is_email_dashboard_enabled
      ? [UnreleasedAPICategory.email as unknown as APICategory]
      : []),
    ...(user?.is_calendar_dashboard_enabled
      ? [UnreleasedAPICategory.calendar as unknown as APICategory]
      : []),
    ...(user?.is_versioncontrol_dashboard_enabled
      ? [UnreleasedAPICategory.versioncontrol as unknown as APICategory]
      : []),
  ];
};

export const categoryDisplayName = (category: APICategory | UnreleasedAPICategory): string => {
  switch (category) {
    case APICategory.hris:
      return "HRIS";
    case APICategory.ats:
      return "ATS";
    case APICategory.accounting:
      return "Accounting";
    case APICategory.ticketing:
      return "Ticketing";
    case APICategory.crm:
      return "CRM";
    case APICategory.filestorage:
      return "File Storage";
    case APICategory.knowledgebase:
      return "Knowledge Base";
    case APICategory.chat:
      return "Chat";
    case APICategory.mktg:
      return "Marketing";
    case UnreleasedAPICategory.datawarehouse:
      return "Data Warehouse";
    case UnreleasedAPICategory.email:
      return "Email";
    case UnreleasedAPICategory.calendar:
      return "Calendar";
    case UnreleasedAPICategory.versioncontrol:
      return "Version Control";
  }
};

export const subCategoriesForAPICategory = (
  category: APICategory | UnreleasedAPICategory,
): string => {
  switch (category) {
    case APICategory.ats:
      return "Applicant tracking systems";
    case APICategory.hris:
      return "HR, payroll, and SCIM directory";
    case APICategory.accounting:
      return "Accounting";
    case APICategory.ticketing:
      return "Ticketing, project management, and help desk";
    case APICategory.crm:
      return "Customer relationship management";
    case APICategory.mktg:
      return "Marketing Automation";
    case APICategory.filestorage:
      return "File Storage";
    case UnreleasedAPICategory.datawarehouse:
      return "Data Warehouse";
    case APICategory.knowledgebase:
      return "Knowledge Base";
    case APICategory.chat:
      return "Chat";
    case UnreleasedAPICategory.email:
      return "Email";
    case UnreleasedAPICategory.calendar:
      return "Calendar";
    case UnreleasedAPICategory.versioncontrol:
      return "Version Control";
  }
};

export const displayNameForAPICategoryIntegration = (
  category: APICategory | UnreleasedAPICategory,
  textVariant?: TextBaseProps["variant"],
  iconSize?: number,
  iconGap: 4 | 8 = 8,
): JSX.Element => {
  const renderContent = (icon: JSX.Element, text: string) => {
    return (
      <div
        className={clsx(
          "flex flex-row whitespace-nowrap items-center",
          iconGap === 4 ? "gap-1" : "gap-2",
        )}
      >
        {icon}
        <Text variant={textVariant}>{text}</Text>
      </div>
    );
  };

  switch (category) {
    case APICategory.ats:
      return renderContent(<UserPlus size={iconSize ?? 16} />, "ATS");
    case APICategory.hris:
      return renderContent(<Users size={iconSize ?? 16} />, "HRIS");
    case APICategory.accounting:
      return renderContent(<DollarSign size={iconSize ?? 16} />, "Accounting");
    case APICategory.ticketing:
      return renderContent(<Ticket size={iconSize ?? 16} />, "Ticketing");
    case APICategory.crm:
      return renderContent(<Calendar size={iconSize ?? 16} />, "CRM");
    case APICategory.mktg:
      return renderContent(<File size={iconSize ?? 16} />, "Marketing");
    case APICategory.filestorage:
      return renderContent(<Folder size={iconSize ?? 16} />, "File Storage");
    case UnreleasedAPICategory.datawarehouse:
      return renderContent(<Database size={iconSize ?? 16} />, "Data Warehouse");
    case APICategory.knowledgebase:
      return renderContent(<Book size={iconSize ?? 16} />, "Knowledge Base");
    case APICategory.chat:
      return renderContent(<MessageCircle size={iconSize ?? 16} />, "Chat");
    case UnreleasedAPICategory.email:
      return renderContent(<Mail size={iconSize ?? 16} />, "Email");
    case UnreleasedAPICategory.calendar:
      return renderContent(<CalendarDays size={iconSize ?? 16} />, "Calendar");
    case UnreleasedAPICategory.versioncontrol:
      return renderContent(<GitBranch size={iconSize ?? 16} />, "Version Control");
  }
};

export const lucideIconForAPICategory = (
  category: APICategory | UnreleasedAPICategory,
  size: "sm" | "md" | "lg" = "md",
  className = "",
): JSX.Element => {
  let sizeNumber = 16;
  switch (size) {
    case "sm":
      sizeNumber = 12;
      break;
    case "md":
      sizeNumber = 16;
      break;
    case "lg":
      sizeNumber = 24;
      break;
  }

  switch (category) {
    case APICategory.hris:
      return <Users size={sizeNumber} className={className} />;
    case APICategory.ats:
      return <UserPlus size={sizeNumber} className={className} />;
    case APICategory.accounting:
      return <DollarSign size={sizeNumber} className={className} />;
    case APICategory.ticketing:
      return <Ticket size={sizeNumber} className={className} />;
    case APICategory.crm:
      return <Contact size={sizeNumber} className={className} />;
    case APICategory.mktg:
      return (
        <MarketingAutomationIcon
          style={{
            minWidth: 16,
          }}
          height={16}
          width={16}
          className={className}
        />
      );
    case APICategory.filestorage:
      return <Folder size={sizeNumber} className={className} />;
    case UnreleasedAPICategory.datawarehouse:
      return <Database size={sizeNumber} className={className} />;
    case APICategory.knowledgebase:
      return <Book size={sizeNumber} className={className} />;
    case APICategory.chat:
      return <MessageCircle size={sizeNumber} className={className} />;
    case UnreleasedAPICategory.email:
      return <Mail size={sizeNumber} className={className} />;
    case UnreleasedAPICategory.calendar:
      return <CalendarDays size={sizeNumber} className={className} />;
    case UnreleasedAPICategory.versioncontrol:
      return <GitBranch size={sizeNumber} className={className} />;
  }
};

export const displayNameForBlueprintSpeed = (blueprintSpeed: BlueprintSpeed): string => {
  switch (blueprintSpeed) {
    case BlueprintSpeed.FAST:
      return "Highest";
    case BlueprintSpeed.INTERMEDIATE:
      return "High";
    case BlueprintSpeed.SLOW:
      return "Standard";
    case BlueprintSpeed.CUSTOM:
      return "Custom";
  }
};

export function formatDate(
  dateString: string | Date,
  formatString = "MMM DD, YYYY hh:mm:ss A",
  includeTimezone = true,
) {
  const userTimezone = moment.tz.guess();
  const convertedDate = moment.utc(dateString).tz(userTimezone);

  const timezoneStr = moment.tz(userTimezone).zoneAbbr();

  const formattedDate = convertedDate.format(formatString);

  return `${formattedDate}${includeTimezone ? " " + timezoneStr : ""}`;
}

export function getAPIRequestPath(endpoint: string, integration: Integration) {
  const path = endpoint.substring(integration.base_api_url.length, endpoint.length);
  return path;
}

export const CATEGORIES_SUPPORTING_CUSTOM_FIELDS = [APICategory.hris];

export const doesUserHavePrivilege = (user: User) =>
  [UserType.admin_with_billing, UserType.admin_without_billing].includes(user.type);

export const displayNameForUserType = (userType: UserType): string => {
  switch (userType) {
    case UserType.member:
      return "Member";
    case UserType.admin_without_billing:
      return "Developer";
    case UserType.admin_with_billing:
      return "Admin";
    case UserType.support:
      return "Support";
  }
};

export const getSanitizedHTML = (html: string) => {
  DOMPurify.addHook("afterSanitizeAttributes", (node) => {
    if ("target" in node) {
      node.setAttribute("target", "_blank");
      node.setAttribute("rel", "noopener noreferrer");
    }
  });

  const cleanHtml = DOMPurify.sanitize(html, { RETURN_DOM_FRAGMENT: true });
  const serializer = new XMLSerializer();
  return serializer.serializeToString(cleanHtml);
};
